import React, { useState, useEffect } from 'react';
import { 
  Box, Container, Typography, Card, CardContent, Grid, TextField, 
  Button, MenuItem, FormControl, InputLabel, Select, Switch, FormControlLabel,
  Divider, Alert, Snackbar, CircularProgress, IconButton, Avatar
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import SettingsIcon from '@mui/icons-material/Settings';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useSystemSettings } from '../components/DynamicThemeProvider';
import { useAuth } from '../context/AuthContext';
import apiClient from '../services/api';

export const SettingsPage: React.FC = () => {
  const { refreshSettings } = useSystemSettings();
  const { user, loading: authLoading } = useAuth();
  
  const [formData, setFormData] = useState({
    institution_name: '',
    institution_type: 'university',
    contact_email: '',
    contact_phone: '',
    brand_color_primary: '#0A2540',
    brand_color_secondary: '#0070E0',
    moodle_url: '',
    moodle_token: '',
    auto_sync: false,
    sync_frequency: 'daily',
  });

  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  useEffect(() => {
    // Only load settings if the logged-in user is an admin
    if (user?.role !== 'admin') {
      setLoading(false);
      return;
    }

    const loadFullSettings = async () => {
      try {
        const response = await apiClient.get('/core/settings/');
        if (response.data) {
          setFormData({
            institution_name: response.data.institution_name || '',
            institution_type: response.data.institution_type || 'university',
            contact_email: response.data.contact_email || '',
            contact_phone: response.data.contact_phone || '',
            brand_color_primary: response.data.brand_color_primary || '#0A2540',
            brand_color_secondary: response.data.brand_color_secondary || '#0070E0',
            moodle_url: response.data.moodle_url || '',
            moodle_token: response.data.moodle_token || '',
            auto_sync: response.data.auto_sync || false,
            sync_frequency: response.data.sync_frequency || 'daily',
          });
          
          if (response.data.logo) {
            setLogoPreview(response.data.logo);
          }
        }
      } catch (err) {
        console.error('Failed to load settings:', err);
        setToast({ open: true, message: 'No se pudieron cargar todas las configuraciones de administrador.', severity: 'error' });
      } finally {
        setLoading(false);
      }
    };

    loadFullSettings();
  }, [user]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSwitchChange = (name: string, checked: boolean) => {
    setFormData(prev => ({ ...prev, [name]: checked }));
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      const data = new FormData();
      Object.entries(formData).forEach(([key, val]) => {
        data.append(key, typeof val === 'boolean' ? String(val) : val);
      });

      if (logoFile) {
        data.append('logo', logoFile);
      }

      await apiClient.patch('/core/settings/', data, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setToast({ open: true, message: 'Configuraciones guardadas con éxito.', severity: 'success' });
      await refreshSettings();
    } catch (err: any) {
      console.error(err);
      const errMsg = err.response?.data?.detail || 'Error al guardar las configuraciones.';
      setToast({ open: true, message: errMsg, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Render auth loading state
  if (authLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress color="secondary" />
      </Box>
    );
  }

  // Web Access Guard for admins only
  if (user?.role !== 'admin') {
    return (
      <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }} className="fade-in-up">
        <Card variant="outlined" sx={{ p: 5, borderRadius: 4, backdropFilter: 'blur(8px)', bgcolor: 'rgba(255, 255, 255, 0.75)' }}>
          <Typography variant="h5" color="error" fontWeight={850} gutterBottom sx={{ letterSpacing: -0.5 }}>
            Acceso Denegado
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph sx={{ maxWidth: '80%', mx: 'auto', mt: 1.5 }}>
            No tienes privilegios de Administrador para ver o modificar los parámetros de marca, colores institucionales o credenciales de la plataforma Moodle.
          </Typography>
          <Button variant="contained" color="secondary" href="/" sx={{ mt: 3, borderRadius: 30, px: 4 }}>
            Volver al Dashboard
          </Button>
        </Card>
      </Container>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress color="secondary" />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" className="fade-in-up" sx={{ py: 4, position: 'relative' }}>
      {/* Background glow nebula */}
      <Box 
        sx={{
          position: 'absolute',
          top: '30%',
          right: '10%',
          width: 450,
          height: 450,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(124,77,255,0.05) 0%, rgba(0,112,224,0.01) 75%)',
          filter: 'blur(50px)',
          zIndex: -1,
          animation: 'breathing-nebula 8s ease-in-out infinite'
        }}
      />

      <Box display="flex" alignItems="center" mb={4}>
        <IconButton href="/" sx={{ mr: 2, bgcolor: 'background.paper', border: '1px solid #E2E8F0' }}>
          <ArrowBackIcon />
        </IconButton>
        <Box>
          <Typography variant="h4" fontWeight={800} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, color: '#0A2540' }}>
            <SettingsIcon color="primary" sx={{ fontSize: 36 }} />
            Parámetros del Sistema
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Modifica la identidad, colores institucionales e integración técnica
          </Typography>
        </Box>
      </Box>

      <form onSubmit={handleSubmit}>
        <Grid container spacing={4}>
          {/* Left panel: Institution Branding */}
          <Grid item xs={12} md={7}>
            <Card variant="outlined" sx={{ mb: 4, borderRadius: 4, backdropFilter: 'blur(8px)', bgcolor: 'rgba(255, 255, 255, 0.8)' }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h6" fontWeight={800} mb={3.5} sx={{ color: '#0A2540' }}>
                  Identidad & Branding
                </Typography>

                <Grid container spacing={3.5}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Nombre de la Institución"
                      name="institution_name"
                      value={formData.institution_name}
                      onChange={handleChange}
                      required
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel id="type-label">Tipo de Institución</InputLabel>
                      <Select
                        labelId="type-label"
                        value={formData.institution_type}
                        label="Tipo de Institución"
                        onChange={(e) => handleSelectChange('institution_type', e.target.value)}
                      >
                        <MenuItem value="university">Universidad</MenuItem>
                        <MenuItem value="institute">Instituto Profesional</MenuItem>
                        <MenuItem value="cft">Centro de Formación Técnica</MenuItem>
                        <MenuItem value="school">Colegio / Liceo</MenuItem>
                        <MenuItem value="corporation">Corporación o Empresa</MenuItem>
                        <MenuItem value="other">Otro</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Box display="flex" alignItems="center" height="100%">
                      <Button
                        variant="outlined"
                        component="label"
                        startIcon={<CloudUploadIcon />}
                        fullWidth
                        sx={{ height: 56, borderStyle: 'dashed', borderRadius: 3 }}
                      >
                        Subir Logotipo
                        <input type="file" hidden accept="image/*" onChange={handleLogoChange} />
                      </Button>
                      {logoPreview && (
                        <Avatar
                          src={logoPreview}
                          variant="rounded"
                          sx={{ 
                            width: 56, 
                            height: 56, 
                            ml: 2, 
                            border: '1px solid #E2E8F0', 
                            objectFit: 'contain',
                            p: 0.5,
                            bgcolor: '#FFFFFF',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.03)'
                          }}
                        />
                      )}
                    </Box>
                  </Grid>

                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Color Primario (Hex)"
                      name="brand_color_primary"
                      value={formData.brand_color_primary}
                      onChange={handleChange}
                      InputProps={{
                        startAdornment: (
                          <Box 
                            sx={{ 
                              width: 20, 
                              height: 20, 
                              borderRadius: '50%', 
                              backgroundColor: formData.brand_color_primary, 
                              mr: 1.5,
                              border: '1px solid #E2E8F0' 
                            }} 
                          />
                        )
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Color Secundario (Hex)"
                      name="brand_color_secondary"
                      value={formData.brand_color_secondary}
                      onChange={handleChange}
                      InputProps={{
                        startAdornment: (
                          <Box 
                            sx={{ 
                              width: 20, 
                              height: 20, 
                              borderRadius: '50%', 
                              backgroundColor: formData.brand_color_secondary, 
                              mr: 1.5,
                              border: '1px solid #E2E8F0' 
                            }} 
                          />
                        )
                      }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            <Card variant="outlined" sx={{ borderRadius: 4, backdropFilter: 'blur(8px)', bgcolor: 'rgba(255, 255, 255, 0.8)' }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h6" fontWeight={800} mb={3.5} sx={{ color: '#0A2540' }}>
                  Contacto Soporte
                </Typography>

                <Grid container spacing={3.5}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Correo de Contacto"
                      name="contact_email"
                      type="email"
                      value={formData.contact_email}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Teléfono de Contacto"
                      name="contact_phone"
                      value={formData.contact_phone}
                      onChange={handleChange}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Right panel: Moodle Integration & Sync */}
          <Grid item xs={12} md={5}>
            <Card variant="outlined" sx={{ height: '100%', borderRadius: 4, backdropFilter: 'blur(8px)', bgcolor: 'rgba(255, 255, 255, 0.8)' }}>
              <CardContent sx={{ p: 4, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h6" fontWeight={800} mb={3.5} sx={{ color: '#0A2540' }}>
                    Integración Moodle Web Services
                  </Typography>

                  <Grid container spacing={3.5}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="URL de Moodle"
                        name="moodle_url"
                        placeholder="https://moodle.tusitio.com"
                        value={formData.moodle_url}
                        onChange={handleChange}
                        required
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Token de Acceso Moodle"
                        name="moodle_token"
                        type="password"
                        placeholder="Ingresa tu token de servicio web"
                        value={formData.moodle_token}
                        onChange={handleChange}
                        required
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }} />
                    </Grid>

                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Switch 
                            checked={formData.auto_sync} 
                            onChange={(e) => handleSwitchChange('auto_sync', e.target.checked)} 
                            color="secondary"
                          />
                        }
                        label={
                          <Typography fontWeight={600} sx={{ color: '#0A2540' }}>
                            Activar Sincronización Automática
                          </Typography>
                        }
                      />
                    </Grid>

                    {formData.auto_sync && (
                      <Grid item xs={12}>
                        <FormControl fullWidth>
                          <InputLabel id="frequency-label">Frecuencia de Sincronización</InputLabel>
                          <Select
                            labelId="frequency-label"
                            value={formData.sync_frequency}
                            label="Frecuencia de Sincronización"
                            onChange={(e) => handleSelectChange('sync_frequency', e.target.value)}
                          >
                            <MenuItem value="hourly">Cada Hora</MenuItem>
                            <MenuItem value="daily">Diario (Cada noche)</MenuItem>
                            <MenuItem value="weekly">Semanal</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    )}
                  </Grid>
                </Box>

                <Box mt={4}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="secondary"
                    fullWidth
                    size="large"
                    disabled={saving}
                    sx={{ 
                      py: 1.8, 
                      borderRadius: 30,
                      fontWeight: 700,
                      background: 'linear-gradient(135deg, #7C4DFF 0%, #0070E0 100%)',
                      boxShadow: '0 4px 16px rgba(124, 77, 255, 0.25)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #6c3ce6 0%, #0060c0 100%)',
                        boxShadow: '0 6px 20px rgba(124, 77, 255, 0.35)'
                      }
                    }}
                  >
                    {saving ? <CircularProgress size={24} color="inherit" /> : 'Guardar Configuraciones'}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </form>

      <Snackbar 
        open={toast.open} 
        autoHideDuration={5000} 
        onClose={() => setToast(prev => ({ ...prev, open: false }))}
      >
        <Alert severity={toast.severity} variant="filled" sx={{ width: '100%' }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default SettingsPage;
